'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Skeleton, Spinner } from '@common/components';
import { Lottie } from '@common/components/atom/Lottie';
import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { Fragment, Suspense, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';
import { AuthSdk, UnauthSdk } from '@/api';
import { HomeFeedNativeAd } from '@/app/_components/ads';
import { CustomLink } from '@/app/_components/CustomLink';
import { DataErrorFallback } from '@/components/client';
import { ROUTES } from '@/constants';
import { useIsGuest } from '@/hooks/useIsGuest';
import { useAuthStore } from '@/stores/AuthStore';
import { isAdSlotPosition } from '@/utils/adSlotPosition';
import { FeedCard, type FeedItem } from './FeedCard';
import { buildFeedBody, buildFeedQueryKey } from './feedQuery';
import { extractSeenIds, getInitialSeenIds, loadSeenFeedIds, mergeSeenIds, saveSeenFeedIds, type SeenFeedIds } from './feedSeenIds';
import { HomeFeedListAdBanner } from './HomeFeedListAdBanner';
const SEEN_FEED_IDS_KEY = 'wello-feed-seen-ids';
type FeedPageWithContents = {
  context?: {
    contents?: readonly FeedItem[];
  } | null;
};
const getPageContents = (page: FeedPageWithContents): FeedItem[] => [...(page.context?.contents ?? [])];
function createFeedScopeKey({
  isLogin,
  codeRegion,
  codeSubRegion
}: FeedCardsProps & {
  isLogin: boolean;
}): string {
  return ['homeFeed', isLogin ? 'auth' : 'guest', codeRegion ?? '', codeSubRegion ?? ''].join(':');
}
function FeedCardSkeleton() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-12"><Skeleton className="size-40 rounded-pill" /><Skeleton className="w-120 rounded-xsmall" typo="caption1" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <article className="bg-Gray-white px-16 py-24 xl:px-24"><div className="flex flex-col gap-20">{t0}<div className="flex flex-col gap-8"><Skeleton className="h-20 w-3/4 rounded-xsmall" /><Skeleton className="h-16 w-full rounded-xsmall" /><Skeleton className="h-16 w-2/3 rounded-xsmall" /></div><Skeleton className="aspect-square w-full rounded-xsmall" /></div></article>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function FeedSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="divide-y divide-Gray-200 bg-Gray-white xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200"><FeedCardSkeleton /><FeedCardSkeleton /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function FeedEndMessage() {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      triggerOnce: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    ref,
    inView
  } = useInView(t0);
  let t1;
  if ($[1] !== inView) {
    t1 = inView && <Lottie loop={false} src="/assets/jsons/check.json" className="size-56" />;
    $[1] = inView;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <div className="size-56">{t1}</div>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex flex-col items-center gap-4"><p className="font-600 text-Gray-900 typo-body2">모든 피드를 확인했어요</p><p className="font-400 text-Gray-700 typo-body4">통합검색에서 다양한 소식을 만나보세요</p></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Button asChild={true} theme="sub-line" className="mt-4 bg-Gray-white" dimensions="H32"><CustomLink href={ROUTES.SEARCH.pathname}>통합검색 바로가기</CustomLink></Button>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== ref || $[8] !== t2) {
    t5 = <div ref={ref} className="flex flex-col items-center gap-12 py-48">{t2}{t3}{t4}</div>;
    $[7] = ref;
    $[8] = t2;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
interface FeedCardsProps {
  codeRegion?: string;
  codeSubRegion?: string;
}
function FeedCards(t0) {
  const $ = _c(59);
  const {
    codeRegion,
    codeSubRegion
  } = t0;
  const isLogin = useAuthStore(_temp);
  const [nextPageError, setNextPageError] = useState(false);
  let t1;
  if ($[0] !== codeRegion || $[1] !== codeSubRegion || $[2] !== isLogin) {
    t1 = () => getInitialSeenIds(createFeedScopeKey({
      isLogin,
      codeRegion,
      codeSubRegion
    }), SEEN_FEED_IDS_KEY);
    $[0] = codeRegion;
    $[1] = codeSubRegion;
    $[2] = isLogin;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [initialSeenIds] = useState(t1);
  let t2;
  if ($[4] !== codeRegion || $[5] !== codeSubRegion) {
    t2 = (page, seenIds) => buildFeedBody({
      page,
      codeRegion,
      codeSubRegion,
      seenIds
    });
    $[4] = codeRegion;
    $[5] = codeSubRegion;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const buildBody = t2;
  let t3;
  if ($[7] !== codeRegion || $[8] !== codeSubRegion || $[9] !== initialSeenIds || $[10] !== isLogin) {
    t3 = buildFeedQueryKey({
      isLogin,
      codeRegion,
      codeSubRegion,
      seenIds: initialSeenIds
    });
    $[7] = codeRegion;
    $[8] = codeSubRegion;
    $[9] = initialSeenIds;
    $[10] = isLogin;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== isLogin) {
    t4 = async t5 => {
      const {
        pageParam,
        signal
      } = t5;
      const sdk = isLogin ? AuthSdk.getFeedList : UnauthSdk.findFeeds;
      const {
        data
      } = await sdk({
        body: pageParam,
        signal,
        throwOnError: true
      });
      return data;
    };
    $[12] = isLogin;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== buildBody || $[15] !== initialSeenIds) {
    t5 = buildBody(1, initialSeenIds);
    $[14] = buildBody;
    $[15] = initialSeenIds;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== buildBody || $[18] !== initialSeenIds) {
    t6 = (t7, allPages) => {
      const {
        context
      } = t7;
      if (!context?.hasNext) {
        return;
      }
      const allContents = allPages.flatMap(getPageContents);
      const seenIds_0 = mergeSeenIds(initialSeenIds, extractSeenIds(allContents));
      return buildBody(allPages.length + 1, seenIds_0);
    };
    $[17] = buildBody;
    $[18] = initialSeenIds;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== t6) {
    t7 = {
      queryKey: t3,
      queryFn: t4,
      gcTime: 600000,
      staleTime: 30000,
      initialPageParam: t5,
      getNextPageParam: t6
    };
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const {
    data: data_0,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useSuspenseInfiniteQuery(t7);
  let t8;
  let t9;
  if ($[25] !== data_0.pages) {
    t8 = () => {
      const allContents_0 = data_0.pages.flatMap(getPageContents);
      const seenIds_1 = mergeSeenIds(loadSeenFeedIds(SEEN_FEED_IDS_KEY), extractSeenIds(allContents_0));
      saveSeenFeedIds(SEEN_FEED_IDS_KEY, seenIds_1);
    };
    t9 = [data_0.pages];
    $[25] = data_0.pages;
    $[26] = t8;
    $[27] = t9;
  } else {
    t8 = $[26];
    t9 = $[27];
  }
  useEffect(t8, t9);
  let t10;
  if ($[28] !== fetchNextPage) {
    t10 = async () => {
      setNextPageError(false);
      try {
        await fetchNextPage({
          cancelRefetch: false
        });
      } catch {
        setNextPageError(true);
      }
    };
    $[28] = fetchNextPage;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const handleFetchNextPage = t10;
  let t11;
  if ($[30] !== handleFetchNextPage || $[31] !== hasNextPage || $[32] !== isFetchingNextPage || $[33] !== nextPageError) {
    t11 = {
      rootMargin: "200px 0px",
      onChange: inView => {
        if (inView && hasNextPage && !isFetchingNextPage && !nextPageError) {
          handleFetchNextPage();
        }
      }
    };
    $[30] = handleFetchNextPage;
    $[31] = hasNextPage;
    $[32] = isFetchingNextPage;
    $[33] = nextPageError;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const {
    ref
  } = useInView(t11);
  const t12 = data_0.pages;
  let hasContents;
  let t13;
  let t14;
  let t15;
  if ($[35] !== data_0.pages) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const flatItems = t12.flatMap(getPageContents);
      hasContents = flatItems.length > 0;
      if (!hasContents) {
        let t16;
        if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
          t16 = <div className="flex items-center justify-center py-64"><p className="font-400 text-Gray-600 typo-body4">아직 피드가 없어요</p></div>;
          $[40] = t16;
        } else {
          t16 = $[40];
        }
        t15 = t16;
        break bb0;
      }
      t13 = "divide-y divide-Gray-200 xl:overflow-hidden xl:rounded-medium xl:border xl:border-Gray-200";
      t14 = flatItems.map((item, itemIndex) => {
        const oneBased = itemIndex + 1;
        const showNative = isAdSlotPosition(oneBased, flatItems.length, {
          firstPosition: 4,
          repeatEvery: 12
        });
        const showBanner = isAdSlotPosition(oneBased, flatItems.length, {
          firstPosition: 10,
          repeatEvery: 12
        });
        return <Fragment key={`feed-${itemIndex}-${item.feedContentType ?? ""}-${item.contentIdx ?? ""}`}>{showNative && <HomeFeedNativeAd />}{showBanner && <HomeFeedListAdBanner />}<FeedCard item={item} priority={itemIndex === 0} /></Fragment>;
      });
    }
    $[35] = data_0.pages;
    $[36] = hasContents;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    hasContents = $[36];
    t13 = $[37];
    t14 = $[38];
    t15 = $[39];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[41] !== hasContents || $[42] !== hasNextPage) {
    t16 = !hasNextPage && hasContents && <FeedEndMessage />;
    $[41] = hasContents;
    $[42] = hasNextPage;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== handleFetchNextPage || $[45] !== hasNextPage || $[46] !== isFetchingNextPage || $[47] !== nextPageError || $[48] !== ref) {
    t17 = hasNextPage && !nextPageError && <div ref={ref} className="flex items-center justify-center p-16">{isFetchingNextPage ? <Spinner /> : <Button theme="sub-line" dimensions="H32" onClick={handleFetchNextPage}>더 불러오기</Button>}</div>;
    $[44] = handleFetchNextPage;
    $[45] = hasNextPage;
    $[46] = isFetchingNextPage;
    $[47] = nextPageError;
    $[48] = ref;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  let t18;
  if ($[50] !== handleFetchNextPage || $[51] !== nextPageError) {
    t18 = nextPageError && <div className="flex flex-col items-center gap-12 p-16"><p className="font-400 text-Gray-600 typo-body4">불러오는 중 문제가 발생했습니다.</p><Button theme="sub-line" dimensions="H32" onClick={handleFetchNextPage}>다시 시도</Button></div>;
    $[50] = handleFetchNextPage;
    $[51] = nextPageError;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  let t19;
  if ($[53] !== t13 || $[54] !== t14 || $[55] !== t16 || $[56] !== t17 || $[57] !== t18) {
    t19 = <div className={t13}>{t14}{t16}{t17}{t18}</div>;
    $[53] = t13;
    $[54] = t14;
    $[55] = t16;
    $[56] = t17;
    $[57] = t18;
    $[58] = t19;
  } else {
    t19 = $[58];
  }
  return t19;
}
function _temp(state) {
  return state.isLogin === true;
}
interface FeedListProps {
  isGuest: boolean;
  codeRegion?: string;
  codeSubRegion?: string;
}
export const FeedList = t0 => {
  const $ = _c(11);
  const {
    isGuest: serverIsGuest,
    codeRegion,
    codeSubRegion
  } = t0;
  const isLogin = useAuthStore(_temp2);
  const isGuest = useIsGuest(serverIsGuest);
  if (!isGuest && isLogin === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FeedSkeleton />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const resolvedIsLogin = !isGuest;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DataErrorFallback />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <FeedSkeleton />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== codeRegion || $[4] !== codeSubRegion || $[5] !== resolvedIsLogin) {
    t3 = createFeedScopeKey({
      isLogin: resolvedIsLogin,
      codeRegion,
      codeSubRegion
    });
    $[3] = codeRegion;
    $[4] = codeSubRegion;
    $[5] = resolvedIsLogin;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== codeRegion || $[8] !== codeSubRegion || $[9] !== t3) {
    t4 = <ErrorBoundary fallback={t1}><Suspense fallback={t2}><FeedCards key={t3} codeRegion={codeRegion} codeSubRegion={codeSubRegion} /></Suspense></ErrorBoundary>;
    $[7] = codeRegion;
    $[8] = codeSubRegion;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
function _temp2(state) {
  return state.isLogin;
}